import axios from 'axios';
import { coreApiClient } from './core';
import type { Chapter, GuidePart } from '@/products/hallouminati/features/CyprusGuide/types';
import type { ApiResponse } from '@/types';

export const guideApi = {
    getChapters: async (): Promise<ApiResponse<GuidePart[]>> => {
        try {
            const response = await coreApiClient.axios.get<{
                data: GuidePart[];
                meta: {
                    total_chapters: number;
                    total_read: number;
                    progress_percentage: number;
                    recent_updates?: Array<{
                        title: string;
                        content: string;
                        date: string;
                        chapter_id?: string;
                    }>;
                }
            }>('/api/hallouminati/guide/chapters');

            // Backend already returns the nested GuidePart structure
            return {
                success: true,
                data: response.data.data,
                meta: response.data.meta
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch chapters'
            };
        }
    },

    getChapterContent: async (chapterId: string, options?: { bustCache?: boolean }): Promise<ApiResponse<Chapter>> => {
        try {
            const response = await coreApiClient.axios.get<{ data: Chapter }>(
                `/api/hallouminati/guide/chapters/${chapterId}`,
                options?.bustCache
                    ? {
                        // Bypass any HTTP/CDN cache when the user explicitly retries
                        params: { _: Date.now() },
                        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
                    }
                    : undefined
            );
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch chapter content'
            };
        }
    },

    markChapterAsRead: async (chapterId: string): Promise<ApiResponse<{ is_read: boolean }>> => {
        try {
            const response = await coreApiClient.axios.post<{ data: { is_read: boolean } }>(`/api/hallouminati/guide/chapters/${chapterId}/read`);
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            // Sentry capture could go here
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to mark chapter as read'
            };
        }
    },

    /**
     * The guide as a PDF. `free` logs the tap and returns the landing's static
     * file; `personal` (Adepts) prints the user's own copy on the server, which
     * takes 15–30 s the first time in a day, and returns a signed link that works for `expires_in` seconds (10 minutes).
     * Contract: docs/hallouminati/guide-print.md
     */
    requestPdf: async (
        edition: 'personal' | 'free',
        lang: 'ru' | 'en',
        platform: string,
    ): Promise<ApiResponse<{ edition: 'personal' | 'free'; url: string; expires_in?: number }>> => {
        try {
            const response = await coreApiClient.axios.post<{ edition: 'personal' | 'free'; url: string; expires_in?: number }>(
                '/api/hallouminati/guide/pdf',
                { edition, lang, platform },
                { timeout: 150_000 },
            );
            return { success: true, data: response.data };
        } catch (error) {
            const serverMessage = axios.isAxiosError<{ message?: string }>(error) ? error.response?.data?.message : undefined;
            return {
                success: false,
                error: serverMessage || (error instanceof Error ? error.message : 'Failed to prepare the PDF'),
            };
        }
    },

    reportIssue: async (chapterId: string, message: string): Promise<ApiResponse<void>> => {
        try {
            await coreApiClient.axios.post(`/api/hallouminati/guide/chapters/${chapterId}/report`, { message });
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to submit report'
            };
        }
    }
};
